import React, { useEffect, useState } from "react";
import axios from "axios";

const API_BASE = process.env.REACT_APP_API_BASE_URL;

const VoyahFreeModsEditor = () => {
  const LANGS = ["En", "Ar", "Ku"];

  const [form, setForm] = useState({
    topTitleEn: "",
    topTitleAr: "",
    topTitleKu: "",

    topSubtitleEn: "",
    topSubtitleAr: "",
    topSubtitleKu: "",

    centerTitleEn: "",
    centerTitleAr: "",
    centerTitleKu: "",

    centerSubtitleEn: "",
    centerSubtitleAr: "",
    centerSubtitleKu: "",

    bottomTitleEn: "",
    bottomTitleAr: "",
    bottomTitleKu: "",

    bottomDescEn: "",
    bottomDescAr: "",
    bottomDescKu: "",
  });

  const [items, setItems] = useState([
    { id: 1, titleEn: "", titleAr: "", titleKu: "", descEn: "", descAr: "", descKu: "", img: "" },
    { id: 2, titleEn: "", titleAr: "", titleKu: "", descEn: "", descAr: "", descKu: "", img: "" },
    { id: 3, titleEn: "", titleAr: "", titleKu: "", descEn: "", descAr: "", descKu: "", img: "" },
    { id: 4, titleEn: "", titleAr: "", titleKu: "", descEn: "", descAr: "", descKu: "", img: "" },
  ]);

  const [images, setImages] = useState({
    mainImage: null,
    bottomImage: null,
    itemsImages: []
  });

  const [loading, setLoading] = useState(true);

  // Modal
  const [showModal, setShowModal] = useState(false);
  const [uploadTarget, setUploadTarget] = useState(null);
  const [uploadFile, setUploadFile] = useState(null);

  const resolveUrl = (u) =>
    u?.startsWith("http") ? u : `${API_BASE}${u}`;

  // =====================================================
  // LOAD EN / AR / KU IN PARALLEL
  // =====================================================
  useEffect(() => {
    const load = async () => {
      try {
        const [en, ar, ku] = await Promise.all([
          axios.get(`${API_BASE}/api/VFModsSection?lang=en`),
          axios.get(`${API_BASE}/api/VFModsSection?lang=ar`),
          axios.get(`${API_BASE}/api/VFModsSection?lang=ku`),
        ]);

        // === MAIN SECTION TEXT ===
        setForm({
          topTitleEn: en.data.topTitle,
          topTitleAr: ar.data.topTitle,
          topTitleKu: ku.data.topTitle,

          topSubtitleEn: en.data.topSubtitle,
          topSubtitleAr: ar.data.topSubtitle,
          topSubtitleKu: ku.data.topSubtitle,

          centerTitleEn: en.data.centerTitle,
          centerTitleAr: ar.data.centerTitle,
          centerTitleKu: ku.data.centerTitle,

          centerSubtitleEn: en.data.centerSubtitle,
          centerSubtitleAr: ar.data.centerSubtitle,
          centerSubtitleKu: ku.data.centerSubtitle,

          bottomTitleEn: en.data.bottomTitle,
          bottomTitleAr: ar.data.bottomTitle,
          bottomTitleKu: ku.data.bottomTitle,

          bottomDescEn: en.data.bottomDesc,
          bottomDescAr: ar.data.bottomDesc,
          bottomDescKu: ku.data.bottomDesc,
        });

        // === ITEMS ===
        const mergedItems = en.data.items.map((itemEn, idx) => ({
          id: itemEn.id,
          titleEn: itemEn.title,
          titleAr: ar.data.items[idx].title,
          titleKu: ku.data.items[idx].title,

          descEn: itemEn.desc,
          descAr: ar.data.items[idx].desc,
          descKu: ku.data.items[idx].desc,

          img: itemEn.img
        }));

        setItems(mergedItems);

        setImages({
          mainImage: en.data.mainImage,
          bottomImage: en.data.bottomImage,
          itemsImages: mergedItems.map(i => i.img)
        });

        setLoading(false);
      } catch (err) {
        console.error("Failed to load VFMods:", err);
      }
    };

    load();
  }, []);

  // =====================================================
  // UPDATE TEXT
  // =====================================================
  const handleSave = async () => {
    const token = localStorage.getItem("token");

    const dto = {
      ...form,
      items: items.map(i => ({
        id: i.id,
        TitleEn: i.titleEn,
        TitleAr: i.titleAr,
        TitleKu: i.titleKu,

        DescEn: i.descEn,
        DescAr: i.descAr,
        DescKu: i.descKu,
      }))
    };

    try {
      await axios.put(`${API_BASE}/api/VFModsSection/1`, dto, {
        headers: { Authorization: `Bearer ${token}` },
      });

      alert("Updated successfully!");
    } catch (err) {
      console.error(err);
      alert("Update failed.");
    }
  };

  // =====================================================
  // UPLOAD MEDIA
  // =====================================================
  const handleUpload = async () => {
    if (!uploadFile) return alert("Select a file first.");

    const token = localStorage.getItem("token");
    const fd = new FormData();

    if (uploadTarget === "main") fd.append("MainImage", uploadFile);
    if (uploadTarget === "bottom") fd.append("BottomImage", uploadFile);
    if (uploadTarget.startsWith("item-")) {
      const idx = uploadTarget.split("-")[1];
      fd.append("ItemsImages", uploadFile);
    }

    try {
      await axios.post(`${API_BASE}/api/VFModsSection/1/media`, fd, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      });

      alert("Media updated!");
      window.location.reload();
    } catch (err) {
      console.error(err);
      alert("Upload failed.");
    }
  };

  const handleTextChange = (e) =>
    setForm({ ...form, [e.target.name]: e.target.value });

  const handleItemChange = (id, field, value) => {
    setItems(items.map(i => i.id === id ? { ...i, [field]: value } : i));
  };

  if (loading) return <p>Loading...</p>;

  return (
    <div className="container py-4">
      <h2 className="fw-bold mb-4">Voyah FREE – Mods Section Editor</h2>

      {/* =========================== MAIN SECTION TEXT ============================== */}
      <h4 className="fw-bold mb-3">Top Section</h4>

      <div className="row">
        {LANGS.map((l) => (
          <div className="col-lg-4 mb-4" key={l}>
            <div className="card shadow-sm border-0">
              <div className="card-body">

                <h5 className="text-primary">{l} Version</h5>

                <label className="form-label">Top Title</label>
                <textarea
                  rows={2}
                  className="form-control mb-2"
                  name={`topTitle${l}`}
                  value={form[`topTitle${l}`]}
                  onChange={handleTextChange}
                />

                <label className="form-label">Top Subtitle</label>
                <textarea
                  rows={3}
                  className="form-control"
                  name={`topSubtitle${l}`}
                  value={form[`topSubtitle${l}`]}
                  onChange={handleTextChange}
                />

              </div>
            </div>
          </div>
        ))}
      </div>

      <h4 className="fw-bold mb-3 mt-4">Center Section</h4>

      <div className="row">
        {LANGS.map((l) => (
          <div className="col-lg-4 mb-4" key={l}>
            <div className="card shadow-sm border-0">
              <div className="card-body">

                <label className="form-label">Center Title</label>
                <input
                  className="form-control mb-2"
                  name={`centerTitle${l}`}
                  value={form[`centerTitle${l}`]}
                  onChange={handleTextChange}
                />

                <label className="form-label">Center Subtitle</label>
                <textarea
                  rows={3}
                  className="form-control"
                  name={`centerSubtitle${l}`}
                  value={form[`centerSubtitle${l}`]}
                  onChange={handleTextChange}
                />

              </div>
            </div>
          </div>
        ))}
      </div>

      <h4 className="fw-bold mb-3 mt-4">Bottom Section</h4>

      <div className="row">
        {LANGS.map((l) => (
          <div className="col-lg-4 mb-4" key={l}>
            <div className="card shadow-sm border-0">
              <div className="card-body">

                <label className="form-label">Bottom Title</label>
                <input
                  className="form-control mb-2"
                  name={`bottomTitle${l}`}
                  value={form[`bottomTitle${l}`]}
                  onChange={handleTextChange}
                />

                <label className="form-label">Bottom Description</label>
                <textarea
                  rows={3}
                  className="form-control"
                  name={`bottomDesc${l}`}
                  value={form[`bottomDesc${l}`]}
                  onChange={handleTextChange}
                />

              </div>
            </div>
          </div>
        ))}
      </div>

      {/* ======================= SAVE MAIN TEXT ======================= */}
      <div className="text-end mb-4">
        <button className="btn btn-primary px-4" onClick={handleSave}>
          Save Text Changes
        </button>
      </div>

      {/* ======================= IMAGES ======================= */}
      <h3 className="fw-bold mt-5">Images</h3>

      {/* MAIN IMAGE */}
      <div className="card mb-4">
        <div className="card-body">
          <h5>Main Image</h5>

          {images.mainImage ? (
            <img
              src={resolveUrl(images.mainImage)}
              className="w-100 rounded mb-3"
              style={{ maxHeight: "300px", objectFit: "cover" }}
            />
          ) : (
            <p>No image uploaded.</p>
          )}

          <button
            className="btn btn-primary"
            onClick={() => {
              setUploadTarget("main");
              setUploadFile(null);
              setShowModal(true);
            }}
          >
            Upload / Replace
          </button>
        </div>
      </div>

      {/* ITEM IMAGES */}
      {items.map((it, idx) => (
        <div className="card mb-4" key={it.id}>
          <div className="card-body">
            <h5>Item #{idx + 1} Image</h5>

            <img
              src={resolveUrl(it.img)}
              className="w-100 rounded mb-3"
              style={{ maxHeight: "260px", objectFit: "cover" }}
            />

            <button
              className="btn btn-primary"
              onClick={() => {
                setUploadTarget(`item-${idx}`);
                setUploadFile(null);
                setShowModal(true);
              }}
            >
              Upload / Replace
            </button>
          </div>
        </div>
      ))}

      {/* BOTTOM IMAGE */}
      <div className="card mb-5">
        <div className="card-body">
          <h5>Bottom Image</h5>

          {images.bottomImage ? (
            <img
              src={resolveUrl(images.bottomImage)}
              className="w-100 rounded mb-3"
              style={{ maxHeight: "280px", objectFit: "cover" }}
            />
          ) : (
            <p>No image uploaded.</p>
          )}

          <button
            className="btn btn-primary"
            onClick={() => {
              setUploadTarget("bottom");
              setUploadFile(null);
              setShowModal(true);
            }}
          >
            Upload / Replace
          </button>
        </div>
      </div>

      {/* ======================= MODAL ======================= */}
      {showModal && (
        <>
          <div className="modal fade show" style={{ display: "block" }}>
            <div className="modal-dialog">
              <div className="modal-content shadow">

                <div className="modal-header">
                  <h5 className="modal-title">Upload File</h5>
                  <button
                    className="btn-close"
                    onClick={() => setShowModal(false)}
                  />
                </div>

                <div className="modal-body">
                  <input
                    type="file"
                    accept="image/*,video/*"
                    className="form-control"
                    onChange={(e) => setUploadFile(e.target.files[0])}
                  />
                </div>

                <div className="modal-footer">
                  <button
                    className="btn btn-secondary"
                    onClick={() => setShowModal(false)}
                  >
                    Cancel
                  </button>

                  <button className="btn btn-primary" onClick={handleUpload}>
                    Upload
                  </button>
                </div>

              </div>
            </div>
          </div>

          <div className="modal-backdrop fade show"></div>
        </>
      )}
    </div>
  );
};

export default VoyahFreeModsEditor;
